import React, { useCallback, useEffect, useRef, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import Container from "@material-ui/core/Container";
import { useDispatch, useSelector } from "react-redux";
import { logOut } from "../auth/authActions";

import {
  any,
  filter,
  includes,
  isEmpty,
  map,
  mergeRight,
  pluck,
  prop,
  sortBy,
  toLower,
} from "ramda";
import {
  useApplicationAccountActivate,
  useApplicationAccountDeactivate,
  useApplicationAccounts,
} from "./applicationAccountServices";
import { fetchSecurityUser } from "../util/fetchUtils";
import { notBlank } from "../util/ext";
import ApplicationAccountList from "./ApplicationAccountList";
import PanelLoadingIndicator from "../components/PanelLoadingIndicator";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import TotalsHeader from "../creator-application/common/TotalsHeader";
import SearchBar from "../creator-application/common/SearchBar";
import EditApplicationAccount from "./EditApplicationAccount";
import ApplicationAccountKeystore from "./ApplicationAccountKeystore";

const useStyles = scrollVisible =>
  makeStyles(theme => ({
    root: {
      border: "3px white solid",
      backgroundColor: "white",
      borderRadius: "20px",
      // padding: "0",
    },

    panel: {
      backgroundColor: "white",
      paddingLeft: theme.spacing(4),
      paddingRight: scrollVisible ? theme.spacing(0) : theme.spacing(4),
    },

    listContainer: {
      height: "calc(100vh - 350px)",
      overflow: "auto",
    },
  }));

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const ApplicationAccountsBrowse = () => {
  const [scrollVisible, setScrollVisible] = useState(true);
  const auth = useSelector(state => state.auth);
  const dispatch = useDispatch();
  const classes = useStyles(scrollVisible)();

  const [initialData, setInitialData] = useState([]);
  const [applicationAccounts, setApplicationAccounts] = useState([]);
  const results = useApplicationAccounts();

  useEffect(() => {
    if (results.isError && results.error.status === 401) {
      dispatch(logOut());
    }

    const fetchUsers = async acct => {
      const createdByUser = await lookupUser(auth, acct.createdBy);
      const updatedByUser = await lookupUser(auth, acct.updatedBy);

      return mergeRight(acct, {
        createdByUsername: createdByUser.username,
        updatedByUsername: updatedByUser.username,
      });
    };

    if (results.isSuccess && notBlank(results.data)) {
      setScrollVisible(true);
      Promise.all(map(fetchUsers, results.data)).then(data => {
        const sortedData = sortBy(prop("accountName"), data);
        setInitialData(sortedData);
        setApplicationAccounts(sortedData);
      });
    }
  }, [
    dispatch,
    auth,
    results.data,
    results.isError,
    results.error,
    results.isSuccess,
  ]);

  //-- Handle New Module Group Dialog
  const [
    applicationAccountDialogOpen,
    setApplicationAccountDialogOpen,
  ] = useState(false);

  const [keystoreDialogOpen, setKeystoreDialogOpen] = useState(false);

  const handleNewApplicationAccountClicked = useCallback(() => {
    setCurrentAccount({});
    setApplicationAccountDialogOpen(true);
  }, []);

  const containsKey = (query, keystore) =>
    any(
      key => includes(toLower(query), toLower(key)),
      pluck("accessKey", keystore),
    );

  //-- Handle Search
  const [appliedQuery, setAppliedQuery] = useState([]);
  const handleQueryChange = updatedQuery => {
    console.log(updatedQuery);
    setAppliedQuery(updatedQuery);
    if (isEmpty(updatedQuery)) {
      setApplicationAccounts(initialData);
    } else {
      setApplicationAccounts(
        filter(
          appAcct =>
            any(
              query =>
                includes(toLower(query), toLower(appAcct.applicationName)) ||
                includes(toLower(query), toLower(appAcct.applicationId)) ||
                containsKey(query, appAcct.keystore),
              updatedQuery,
            ),
          initialData,
        ),
      );
    }
  };

  const [currentAccount, setCurrentAccount] = useState({});
  const handleAccountEdit = useCallback(account => {
    setCurrentAccount(account);
    setApplicationAccountDialogOpen(true);
  }, []);

  const handleApplicationAccountEditClose = useCallback(() => {
    setApplicationAccountDialogOpen(false);
    setCurrentAccount({});
  }, []);

  const handleApplicationAccountEditSave = useCallback(() => {
    setApplicationAccountDialogOpen(false);
    setCurrentAccount({});
    results.refetch();
  }, [results]);

  const handleShowKeys = useCallback(account => {
    setCurrentAccount(account);
    setKeystoreDialogOpen(true);
  }, []);

  const handleKeystoreClose = useCallback(() => {
    setCurrentAccount([]);
    setKeystoreDialogOpen(false);
  }, []);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl) && notBlank(tableContainerEl.current)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      console.log(`Scroll Visible : ${scrollVisible}`);
      setScrollVisible(scrollVisible);
    }
  }, [tableContainerEl]);

  const accountDeactivate = useApplicationAccountDeactivate();
  const accountActivate = useApplicationAccountActivate();
  const handleActiveToggle = useCallback(
    async account => {
      console.log(account);

      const id = {
        resourceIds: { id: account.applicationId },
      };

      if (account.active) {
        await accountDeactivate.mutateAsync(id);
      } else {
        await accountActivate.mutateAsync(id);
      }
      await results.refetch();
    },
    [accountDeactivate, accountActivate, results],
  );

  return (
    <Container
      id="application-account-browse-view"
      classes={{ root: classes.root }}
    >
      <EditApplicationAccount
        open={applicationAccountDialogOpen}
        account={currentAccount}
        onClose={handleApplicationAccountEditClose}
        onSave={handleApplicationAccountEditSave}
      />

      <ApplicationAccountKeystore
        account={currentAccount}
        open={keystoreDialogOpen}
        onClose={handleKeystoreClose}
      />

      <div>
        <SearchBar query={appliedQuery} onQueryChange={handleQueryChange} />

        <TotalsHeader
          countText="application account"
          count={results.isSuccess ? applicationAccounts.length : "-"}
          selectedActions={
            <div>
              <Button
                variant={"text"}
                startIcon={<AddIcon />}
                onClick={handleNewApplicationAccountClicked}
              >
                New Application Account
              </Button>
            </div>
          }
        />

        {results.isLoading && <PanelLoadingIndicator />}
        {results.isSuccess && notBlank(applicationAccounts) && (
          <Container classes={{ root: classes.panel }}>
            <Container ref={tableContainerEl} className={classes.listContainer}>
              <ApplicationAccountList
                accounts={applicationAccounts}
                onActiveToggle={handleActiveToggle}
                onEdit={handleAccountEdit}
                onShowKeys={handleShowKeys}
              />
            </Container>
          </Container>
        )}
      </div>
    </Container>
  );
};

export default ApplicationAccountsBrowse;
